import { useCallback, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { DeleteOutline, UploadFile } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WaveformPlayer from './WaveformPlayer';

interface WaveformPlayerFieldProps {
  url?: string | null;
  displayName?: string | null;
  downloadFilename?: string | null;
  durationSeconds?: number | null;
  displayNameEditable?: boolean;
  displayNameSaving?: boolean;
  disabled?: boolean;
  required?: boolean;
  uploadEnabled?: boolean;
  isUploading?: boolean;
  uploadProgress?: number | null;
  accept?: string;
  emptyLabel?: string;
  constraints?: {
    maxFileSizeBytes?: number;
    minFileSizeBytes?: number;
    maxDurationSeconds?: number;
    minDurationSeconds?: number;
    allowedMimeTypes?: string[];
  };
  showConstraints?: boolean;
  onDisplayNameCommit?: (nextDisplayName: string) => void | Promise<void>;
  onSelectFile?: (file: File) => void | Promise<void>;
  onRemove?: () => void | Promise<void>;
}

/**
 * Field wrapper around WaveformPlayer with optional dropzone/upload controls.
 * Keeps upload interaction reusable while preserving WaveformPlayer as playback-only.
 */
export default function WaveformPlayerField({
  url,
  displayName,
  downloadFilename,
  durationSeconds = null,
  displayNameEditable = false,
  displayNameSaving = false,
  disabled = false,
  required = false,
  uploadEnabled = false,
  isUploading = false,
  uploadProgress = null,
  accept = 'audio/*',
  emptyLabel,
  constraints,
  showConstraints = true,
  onDisplayNameCommit,
  onSelectFile,
  onRemove,
}: WaveformPlayerFieldProps) {
  const { t } = useTranslation(['components', 'common']);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const hasFile = Boolean(url);
  const canUpload = uploadEnabled && !disabled && typeof onSelectFile === 'function';
  const canRemove = !required && !disabled && typeof onRemove === 'function';

  const currentEmptyLabel =
    emptyLabel || t('components:waveformPlayerField.empty', 'No audio file selected.');

  const triggerFileDialog = useCallback(() => {
    if (!canUpload || isUploading) {
      return;
    }
    inputRef.current?.click();
  }, [canUpload, isUploading]);

  const formatBytes = useCallback((bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / 1024 ** exponent;
    return `${size.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
  }, []);

  const formatDurationLabel = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins <= 0) {
      return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
  }, []);

  const getAudioDurationSeconds = useCallback((file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      const objectUrl = URL.createObjectURL(file);
      audio.preload = 'metadata';
      audio.src = objectUrl;
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        if (!Number.isFinite(audio.duration)) {
          reject(new Error('Invalid audio duration'));
          return;
        }
        resolve(audio.duration);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load audio metadata'));
      };
    });
  }, []);

  /**
   * Applies prop-driven validation rules before handing file to upload callback.
   */
  const validateSelectedFile = useCallback(
    async (file: File): Promise<string | null> => {
      if (!constraints) {
        return null;
      }
      if (
        constraints.allowedMimeTypes &&
        constraints.allowedMimeTypes.length > 0 &&
        !constraints.allowedMimeTypes.includes(file.type)
      ) {
        return t('components:waveformPlayerField.errors.invalidMimeType', 'Unsupported file type.');
      }
      if (
        Number.isFinite(constraints.maxFileSizeBytes) &&
        constraints.maxFileSizeBytes !== undefined &&
        file.size > constraints.maxFileSizeBytes
      ) {
        return t('components:waveformPlayerField.errors.maxFileSize', 'File exceeds maximum size of {{value}}.', {
          value: formatBytes(constraints.maxFileSizeBytes),
        });
      }
      if (
        Number.isFinite(constraints.minFileSizeBytes) &&
        constraints.minFileSizeBytes !== undefined &&
        file.size < constraints.minFileSizeBytes
      ) {
        return t('components:waveformPlayerField.errors.minFileSize', 'File is smaller than {{value}}.', {
          value: formatBytes(constraints.minFileSizeBytes),
        });
      }
      if (
        constraints.maxDurationSeconds !== undefined ||
        constraints.minDurationSeconds !== undefined
      ) {
        const durationSeconds = await getAudioDurationSeconds(file);
        if (
          Number.isFinite(constraints.maxDurationSeconds) &&
          constraints.maxDurationSeconds !== undefined &&
          durationSeconds > constraints.maxDurationSeconds
        ) {
          return t(
            'components:waveformPlayerField.errors.maxDuration',
            'Audio exceeds maximum duration of {{value}}.',
            { value: formatDurationLabel(constraints.maxDurationSeconds) }
          );
        }
        if (
          Number.isFinite(constraints.minDurationSeconds) &&
          constraints.minDurationSeconds !== undefined &&
          durationSeconds < constraints.minDurationSeconds
        ) {
          return t(
            'components:waveformPlayerField.errors.minDuration',
            'Audio is shorter than {{value}}.',
            { value: formatDurationLabel(constraints.minDurationSeconds) }
          );
        }
      }
      return null;
    },
    [constraints, formatBytes, formatDurationLabel, getAudioDurationSeconds, t]
  );

  /**
   * Normalizes file selection from input or drag-and-drop and delegates upload.
   */
  const submitFile = useCallback(
    async (file: File | null) => {
      if (!file || !onSelectFile) {
        return;
      }
      setLocalError(null);
      try {
        const validationError = await validateSelectedFile(file);
        if (validationError) {
          setLocalError(validationError);
          return;
        }
        await onSelectFile(file);
      } catch (error) {
        setLocalError(
          t('components:waveformPlayerField.errors.uploadFailed', 'Failed to upload audio file.')
        );
        console.error('WaveformPlayerField upload failed:', error);
      }
    },
    [onSelectFile, t, validateSelectedFile]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0] || null;
      void submitFile(selected);
      event.target.value = '';
    },
    [submitFile]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);
      if (!canUpload || isUploading) {
        return;
      }
      const droppedFile = event.dataTransfer.files?.[0] || null;
      void submitFile(droppedFile);
    },
    [canUpload, isUploading, submitFile]
  );

  const dropzoneSx = useMemo(
    () => ({
      p: 2.5,
      border: '1px dashed',
      borderColor: isDragActive ? 'primary.main' : 'divider',
      bgcolor: isDragActive ? 'action.hover' : 'background.default',
      borderRadius: 2,
      cursor: canUpload && !isUploading ? 'pointer' : 'default',
      textAlign: 'center',
      transition: 'all 0.15s ease',
    }),
    [isDragActive, canUpload, isUploading]
  );

  return (
    <Stack spacing={1.25}>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        onChange={handleInputChange}
      />

      {hasFile ? (
        <>
          <WaveformPlayer
            url={url as string}
            displayName={displayName || undefined}
            downloadFilename={downloadFilename || undefined}
            durationSeconds={durationSeconds}
            displayNameEditable={displayNameEditable}
            displayNameSaving={displayNameSaving}
            onDisplayNameCommit={onDisplayNameCommit}
          />
          <Stack direction="row" spacing={1}>
            {canUpload && (
              <Button
                variant="outlined"
                startIcon={isUploading ? <CircularProgress color="inherit" size={16} /> : <UploadFile />}
                disabled={isUploading}
                onClick={triggerFileDialog}
              >
                {isUploading
                  ? t('components:waveformPlayerField.uploading', 'Uploading...')
                  : t('components:waveformPlayerField.replace', 'Replace')}
              </Button>
            )}
            {canRemove && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                disabled={isUploading}
                onClick={() => void onRemove?.()}
              >
                {t('components:waveformPlayerField.remove', 'Remove')}
              </Button>
            )}
          </Stack>
        </>
      ) : (
        <Box
          role="button"
          tabIndex={canUpload ? 0 : -1}
          sx={dropzoneSx}
          onClick={triggerFileDialog}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (canUpload && !isUploading) {
              setIsDragActive(true);
            }
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onDrop={handleDrop}
          onKeyDown={(event) => {
            if (!canUpload || isUploading) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              triggerFileDialog();
            }
          }}
        >
          <Stack spacing={0.75} alignItems="center">
            <UploadFile color={canUpload ? 'primary' : 'disabled'} />
            <Typography variant="body2" color="text.primary">
              {canUpload
                ? t(
                    'components:waveformPlayerField.dropzone',
                    'Click or drag an audio file here.'
                  )
                : currentEmptyLabel}
            </Typography>
            {canUpload && (
              <Typography variant="caption" color="text.secondary">
                {t('components:waveformPlayerField.allowedTypes', 'Accepted: audio files')}
              </Typography>
            )}
            {isUploading && (
              <Typography variant="caption" color="text.secondary">
                {t('components:waveformPlayerField.uploading', 'Uploading...')}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {localError && <Alert severity="error">{localError}</Alert>}
      {showConstraints && constraints && (
        <Stack spacing={0.25}>
          {constraints.maxFileSizeBytes !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t(
                'components:waveformPlayerField.constraints.maxFileSize',
                'Max file size: {{value}}',
                { value: formatBytes(constraints.maxFileSizeBytes) }
              )}
            </Typography>
          )}
          {constraints.minFileSizeBytes !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t(
                'components:waveformPlayerField.constraints.minFileSize',
                'Min file size: {{value}}',
                { value: formatBytes(constraints.minFileSizeBytes) }
              )}
            </Typography>
          )}
          {constraints.maxDurationSeconds !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t(
                'components:waveformPlayerField.constraints.maxDuration',
                'Max duration: {{value}}',
                { value: formatDurationLabel(constraints.maxDurationSeconds) }
              )}
            </Typography>
          )}
          {constraints.minDurationSeconds !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t(
                'components:waveformPlayerField.constraints.minDuration',
                'Min duration: {{value}}',
                { value: formatDurationLabel(constraints.minDurationSeconds) }
              )}
            </Typography>
          )}
        </Stack>
      )}
      {isUploading && (
        <Stack spacing={0.5}>
          <LinearProgress
            variant={uploadProgress !== null ? 'determinate' : 'indeterminate'}
            value={uploadProgress ?? undefined}
          />
          {uploadProgress !== null && (
            <Typography variant="caption" color="text.secondary">
              {t('components:waveformPlayerField.progress', '{{value}}%', {
                value: uploadProgress.toFixed(2),
              })}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
